import { useQuery, type UseQueryResult } from '@tanstack/react-query';
import ms from 'ms';
import { z } from 'zod';
import { ENDPOINT } from '~/constants';

import { vestigasApi } from '~/services/kyClient';

import { log } from '~/utils';

import { queryKeysCompany } from './queryKeys';

/**
 * `SenderCompany` in the OpenAPI spec.
 */
const zodSendingCompanySchema = z.object({
  companyAccountId: z.uuid(),
  id: z.uuid(),
  name: z.string(),
});

const zodSendingCompaniesResponseSchema = z.array(zodSendingCompanySchema);

export type SendingCompany = z.infer<typeof zodSendingCompanySchema>;

/**
 * Fetch all supplier alert configurations.
 *
 * @see https://app.dev.vestigas.com/redoc#tag/Company/operation/get_sending_companies_company_sending_companies_get
 */
const fetchSendingCompanies = async ({ signal }: { signal: AbortSignal }) => {
  try {
    return await vestigasApi
      .get(ENDPOINT.COMPANY.SUPPLIER_ALERT_CONFIGS.GET_ALL(), {
        signal,
        validate: { response: zodSendingCompaniesResponseSchema },
      })
      .json<SendingCompany[]>();
  } catch (error) {
    log.error({
      message: 'Error fetching sending companies',
      error,
    });

    // Re-throw the error to propagate it to the calling function.
    throw error;
  }
};

/**
 * A custom hook to fetch all all sending companies with at least one active data exchange.
 * Uses the `useQuery` hook from `@tanstack/react-query` internally.
 *
 * @see https://app.dev.vestigas.com/redoc#tag/Company/operation/get_sending_companies_company_sending_companies_get
 */
export const useQuerySendingCompanies = (
  options = {},
): UseQueryResult<SendingCompany[]> =>
  useQuery({
    queryFn: async ({ signal }) => fetchSendingCompanies({ signal }),
    queryKey: queryKeysCompany.getSendingCompanies(),
    staleTime: ms('60s'),
    ...options,
  });
